"use client";
import { useDisclosureProviderContext } from "./disclosure-context.js";
import { DialogScopedContextProvider } from "../dialog/dialog-context.js";
import { createElement, createHook, forwardRef, useId, useMergeRefs, useSafeLayoutEffect, useWrapElement } from "@ariakit/react-utils";
import { afterPaint, invariant, removeUndefinedValues } from "@ariakit/utils";
import { useMemo, useRef, useState } from "react";
import { jsx } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
import { flushSync } from "react-dom";
//#region src/disclosure/disclosure-content.tsx
const TagName = "div";
function afterTimeout(timeoutMs, cb) {
	const timeoutId = setTimeout(cb, timeoutMs);
	return () => clearTimeout(timeoutId);
}
function parseCSSTime(time) {
	const value = time?.trim() || "0s";
	const multiplier = value.endsWith("ms") ? 1 : 1e3;
	const parsed = Number.parseFloat(value) * multiplier;
	return Number.isNaN(parsed) ? 0 : parsed;
}
function getEndTime(names, delays, durations) {
	const nameList = names.split(",");
	const delayList = delays.split(",");
	const durationList = durations.split(",");
	let endTime = 0;
	for (const [index, name] of nameList.entries()) {
		if (name.trim() === "none") continue;
		const delay = parseCSSTime(delayList[index % delayList.length]);
		const duration = parseCSSTime(durationList[index % durationList.length]);
		endTime = Math.max(endTime, delay + duration);
	}
	return endTime;
}
function getElementEndTime(element) {
	const { transitionProperty, transitionDuration, transitionDelay, animationName, animationDuration, animationDelay } = getComputedStyle(element);
	return Math.max(getEndTime(transitionProperty, transitionDelay, transitionDuration), getEndTime(animationName, animationDelay, animationDuration));
}
function isHidden(mounted, hidden, alwaysVisible) {
	return !alwaysVisible && hidden !== false && (!mounted || !!hidden);
}
/**
* Returns props to create a `DislosureContent` component.
* @see https://ariakit.com/components/disclosure
* @example
* ```jsx
* const store = useDisclosureStore();
* const props = useDisclosureContent({ store });
* <Disclosure store={store}>Disclosure</Disclosure>
* <Role {...props}>Content</Role>
* ```
*/
const useDisclosureContent = createHook(function useDisclosureContent({ store, alwaysVisible, unstable_otherElementRef: otherElementRef, ...props }) {
	const context = useDisclosureProviderContext();
	store = store || context;
	invariant(store, process.env.NODE_ENV !== "production" && "DisclosureContent must receive a `store` prop or be wrapped in a DisclosureProvider component.");
	const ref = useRef(null);
	const id = useId(props.id);
	const [transition, setTransition] = useState(null);
	const open = useStoreState(store, "open");
	const mounted = useStoreState(store, "mounted");
	const animated = useStoreState(store, "animated");
	const contentElement = useStoreState(store, "contentElement");
	const otherElement = useStoreState(store.disclosure, "contentElement");
	const hasClosedRef = useRef(false);
	useSafeLayoutEffect(() => {
		if (!ref.current) return;
		store?.setContentElement(ref.current);
	}, [store]);
	useSafeLayoutEffect(() => {
		let previousAnimated;
		store?.setState("animated", (animated) => {
			previousAnimated = animated;
			return true;
		});
		return () => {
			if (previousAnimated === void 0) return;
			store?.setState("animated", previousAnimated);
		};
	}, [store]);
	useSafeLayoutEffect(() => {
		if (!animated) {
			if (!open) {
				hasClosedRef.current = true;
				setTransition(null);
			} else if (hasClosedRef.current) {
				hasClosedRef.current = false;
				setTransition("enter");
			}
			return;
		}
		if (!contentElement?.isConnected) {
			setTransition(null);
			return;
		}
		return afterPaint(() => {
			setTransition(open ? "enter" : mounted ? "leave" : null);
		});
	}, [
		animated,
		contentElement,
		open,
		mounted
	]);
	useSafeLayoutEffect(() => {
		if (!store) return;
		if (!animated) return;
		if (!transition) return;
		if (!contentElement) return;
		const stopAnimation = () => store?.setState("animating", false);
		const stopAnimationSync = () => flushSync(stopAnimation);
		if (transition === "leave" && open) return;
		if (transition === "enter" && !open) return;
		if (typeof animated === "number") return afterTimeout(animated, stopAnimationSync);
		const elements = [contentElement];
		if (otherElement) elements.push(otherElement);
		const relatedElement = otherElementRef?.current;
		if (relatedElement) elements.push(relatedElement);
		const timeout = Math.max(...elements.map(getElementEndTime));
		if (!timeout) {
			if (transition === "enter") store.setState("animated", false);
			stopAnimation();
			return;
		}
		return afterTimeout(Math.max(timeout - 1e3 / 60, 0), stopAnimationSync);
	}, [
		store,
		animated,
		contentElement,
		otherElement,
		otherElementRef,
		open,
		transition
	]);
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(DialogScopedContextProvider, {
		value: store,
		children: element
	}), [store]);
	const hidden = isHidden(mounted, props.hidden, alwaysVisible);
	const styleProp = props.style;
	const style = useMemo(() => {
		if (hidden) return {
			...styleProp,
			display: "none"
		};
		return styleProp;
	}, [hidden, styleProp]);
	props = {
		"data-open": open || void 0,
		"data-enter": transition === "enter" || void 0,
		"data-leave": transition === "leave" || void 0,
		hidden,
		...props,
		id,
		ref: useMergeRefs(id ? store.setContentElement : null, ref, props.ref),
		style
	};
	return removeUndefinedValues(props);
});
const DisclosureContentImpl = forwardRef(function DisclosureContentImpl(props) {
	return createElement(TagName, useDisclosureContent(props));
});
/**
* Renders an element that can be shown or hidden by a
* [`Disclosure`](https://ariakit.com/components/disclosure) component.
* @see https://ariakit.com/components/disclosure
* @example
* ```jsx {3}
* <DisclosureProvider>
*   <Disclosure>Disclosure</Disclosure>
*   <DisclosureContent>Content</DisclosureContent>
* </DisclosureProvider>
* ```
*/
const DisclosureContent = forwardRef(function DisclosureContent({ unmountOnHide, ...props }) {
	const context = useDisclosureProviderContext();
	if (useStoreState(props.store || context, (state) => !unmountOnHide || state?.mounted) === false) return null;
	return /* @__PURE__ */ jsx(DisclosureContentImpl, { ...props });
});
//#endregion
export { DisclosureContent, isHidden, useDisclosureContent };

//# sourceMappingURL=disclosure-content.js.map